// Подключение push этого телефона к вошедшему человеку (B-039, клиентская часть T-046).
//
// Выбор «присылать push» хранит сервер у человека, адрес FCM — у установки. Связывает их
// этот хук: в каждой сессии телефон привязывает свой адрес к ней — при любом выборе,
// доставку решает сервер, — а при включённом выборе ещё берёт свежий адрес у Google
// и, когда это уместно, спрашивает разрешение на уведомления. Каждый вход — пароль, PIN,
// ссылка — даёт новый токен, каждый запуск — новый процесс; поэтому отдельного вызова
// ни в экранах входа, ни в `main.tsx` не нужно.
//
//   выбор включён  → разрешение (см. `shouldAsk`), канал, свежий адрес, `sync`
//   выбор выключен → `sync` известного адреса, FCM не будится; адреса нет — ничего
//
// Прогон повторяется и когда выбор меняется — галочкой в окне этого или другого телефона,
// замеченной перезапросом настроек. Поэтому окну достаточно сохранить выбор: push
// подключится, даже если окно закрыли, не дождавшись ответа (ревью B-039).
//
// Не трогает push: планшет Windows и браузер (push там нет), саппорт-сессия (сессия
// чужая, телефон — платформенного админа), прежний сервер (404): там push этот бандл
// не предлагает вовсе.

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useSyncExternalStore } from 'react';

import { getToken, loadKioskBinding, useSupportSession } from '@/shared/auth';
import { connectThisPhone, pushSupported, setPushHere, setPushRetryHandler } from '@/shared/push';

import { alertSettingsKey, isLegacy, useAlertSettings } from './api';

/** Повтор недошедшего прогона, пока сеть «есть», но не отвечает (как фоновая проверка очереди). */
const RETRY_MS = 30_000;

// Состояние — в памяти модуля, а не компонента: layout монтируется заново на переходах
// и в StrictMode, а прогон обязан случиться один раз на сессию и выбор. Токен сравнивается
// здесь и никуда не записывается — ни в ключ запроса, ни на диск.

/** Сессия, которую видит хук, и когда он её увидел: данные старше — не про неё. */
let session: { readonly token: string; readonly since: number } | null = null;
/** Выбор, для которого в этой сессии прогон прошёл или идёт. */
let settledChoice: boolean | null = null;
/** В этой сессии уже был прогон: сам спрашивает разрешение только первый. */
let startedInSession = false;
/** Человек включил push в окне ЭТОГО телефона: прогон, который это заметит, спросит разрешение. */
let askRequested = false;

/** Прогон не дошёл до сервера — повторить без запроса разрешения. Таймер живёт, пока это так. */
let retryWanted = false;
const retryListeners = new Set<() => void>();

function setRetryWanted(next: boolean): void {
  if (retryWanted === next) return;
  retryWanted = next;
  for (const listener of retryListeners) listener();
}

function subscribeRetry(listener: () => void): () => void {
  retryListeners.add(listener);
  return () => {
    retryListeners.delete(listener);
  };
}

function readRetry(): boolean {
  return retryWanted;
}

/** Прогон этой сессии не дошёл до сервера: повторить, если сессия всё ещё текущая. */
function retryLater(owner: typeof session): void {
  if (owner === null || session !== owner) return;
  settledChoice = null;
  setRetryWanted(true);
}

/** Закреплённое устройство (T-040) — общий планшет или телефон точки. */
async function pinnedHere(): Promise<boolean> {
  try {
    return (await loadKioskBinding()) !== null;
  } catch {
    return true;
  }
}

/**
 * Спросить ли разрешение самому. Только явное включение в окне этого телефона или первый
 * прогон сессии на ЛИЧНОМ телефоне (переустановка, новый телефон — B-039, сценарий 6).
 * На закреплённом устройстве запрос сам не всплывает: там работают по очереди, и системное
 * окно поверх экрана печати ничьё. Повтор не спрашивает никогда — первый прогон уже спросил.
 */
async function shouldAsk(first: boolean, retry: boolean): Promise<boolean> {
  if (retry) return false;
  if (askRequested) return true;
  return first && !(await pinnedHere());
}

async function runForChoice(enabled: boolean, owner: NonNullable<typeof session>): Promise<void> {
  const retry = retryWanted;
  const first = !startedInSession;
  startedInSession = true;
  const ask = enabled && (await shouldAsk(first, retry));
  askRequested = false;
  if (session !== owner) return;
  const registered = await connectThisPhone(enabled ? { ask } : { ask: false, quiet: true });
  if (session !== owner) return;
  setRetryWanted(false);
  // Сервер знает выбор новее окна: его сменили с другого телефона, пока шёл запрос.
  if (registered !== null && registered !== enabled) settingsOutdated?.();
}

/** Перечитать настройки — их держит хук, у модуля своего клиента запросов нет. */
let settingsOutdated: (() => void) | null = null;

/** Монтируется ОДИН раз — в `AppLayout`, рядом с двигателем очереди. */
export function usePushSync(): void {
  const queryClient = useQueryClient();
  const supported = pushSupported();
  const supportSession = useSupportSession() !== null;
  const active = supported && !supportSession;
  const token = getToken();
  const settings = useAlertSettings({ enabled: active });
  const retrying = useSyncExternalStore(subscribeRetry, readRetry, readRetry);
  const [attempt, setAttempt] = useState(0);

  // Новая сессия — вход, запуск, начало и конец саппорт-сессии. Всё, что хук знал
  // о прежней, — не про неё: и прогон, и строка под галочкой, и настройки в кэше. Они
  // перезапрашиваются, а прогон ждёт ответа, полученного уже в этой сессии. Иначе после
  // конца саппорт-сессии телефон подключался бы по настройкам того, чьими глазами смотрели.
  useEffect(() => {
    if (!supported || session?.token === token) return;
    if (token === null) {
      // Вышли: токен ушедшего в памяти модуля не держится.
      session = null;
      return;
    }
    session = { token, since: Date.now() };
    settledChoice = null;
    startedInSession = false;
    askRequested = false;
    setRetryWanted(false);
    setPushHere(null);
    if (active) {
      // Идущий запрос не отменяется: он ушёл уже с этим токеном. Запросы прежней сессии
      // уходят вместе с кэшем — его чистят выход, вход другого человека и конец
      // саппорт-сессии, — а мёртвая сессия данных не приносит (401).
      void queryClient.invalidateQueries({ queryKey: alertSettingsKey }, { cancelRefetch: false });
    }
  }, [supported, active, token, queryClient]);

  useEffect(() => {
    if (!active) return undefined;
    settingsOutdated = () => void queryClient.invalidateQueries({ queryKey: alertSettingsKey });
    // Смена адреса Google, не дошедшая до сервера, — тот же повтор прогона.
    setPushRetryHandler(() => retryLater(session));
    return () => {
      settingsOutdated = null;
      setPushRetryHandler(null);
    };
  }, [active, queryClient]);

  const data = settings.data;
  const updatedAt = settings.dataUpdatedAt;
  useEffect(() => {
    const owner = session;
    if (!active || token === null || owner === null || owner.token !== token) return;
    if (data === undefined || updatedAt < owner.since || isLegacy(data)) return;
    const enabled = data.push.enabled;
    if (settledChoice === enabled) return;
    settledChoice = enabled;
    // Отклоняется только недошедшим `sync`; ответ сервера (401, 400) повторять
    // бессмысленно: мёртвую сессию разберёт `AppLayout`, а вход даст новую.
    runForChoice(enabled, owner).catch(() => retryLater(owner));
  }, [active, token, data, updatedAt, attempt]);

  // Таймер — только пока повтор нужен. Кроме события `online` — таймер: при зависшей
  // сети `navigator.onLine` не меняется, и события не бывает вовсе (`useSync.ts`).
  useEffect(() => {
    if (!active || !retrying) return undefined;
    const again = (): void => setAttempt((value) => value + 1);
    const tick = (): void => {
      if (navigator.onLine) again();
    };
    window.addEventListener('online', again);
    const timer = setInterval(tick, RETRY_MS);
    return () => {
      window.removeEventListener('online', again);
      clearInterval(timer);
    };
  }, [active, retrying]);
}

/**
 * Человек включил push в окне ЭТОГО телефона: прогон, который заметит включённый выбор,
 * спросит разрешение — и на закреплённом устройстве тоже, ведь нажал он сам.
 */
export function askOnNextConnect(): void {
  askRequested = true;
}

/**
 * «Разрешить уведомления» в окне: спросить разрешение и подключить телефон заново.
 * Недошедший `sync` повторит хук — разрешение к тому времени уже спрошено.
 */
export async function allowFromDialog(): Promise<void> {
  const owner = session;
  try {
    await connectThisPhone({ ask: true });
  } catch {
    retryLater(owner);
  }
}

/** Только для тестов: состояние модуля между тестами не переживает. */
export function __resetPushSyncForTests(): void {
  session = null;
  settledChoice = null;
  startedInSession = false;
  askRequested = false;
  settingsOutdated = null;
  setRetryWanted(false);
}
